import useSWR from "swr";
import { API_ENDPOINT } from "../abis/constants";
import { BrowserProvider, Eip1193Provider, ethers } from "ethers";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSwitchNetwork, useWeb3ModalAccount, useWeb3ModalProvider } from "@web3modal/ethers/react";
import { useGlobalState } from "@reactivers/hooks";

interface Chain {
  name: string,
  network: string,
  chainId: number,
  currency: string,
  rpcUrl: string,
  explorerUrl: string,
  contractAddress: `0x${string}`,
  abi: ethers.Interface | ethers.InterfaceAbi,
  virtualEthAmount: number,
  virtualTokenAmount: number,
  totalSupply: number,
  targetMarketCap: number,
  testnet?: boolean,
}

export function useChains() {
  return useSWR(
    '/info/chain',
    async () => {
      const { data } = await axios.get(`${API_ENDPOINT}/config`)
      return data?.chains as Chain[]
    }, {
      // refreshInterval: 60000,
    }
  )
}

export function useChainInfo() {
  const { data: chains } = useChains()
  // const { globalState, setGlobalState } = useGlobalState()
  const { chainId } = useWeb3ModalAccount()
  
  const chain = useMemo(() => chains?.find((c: Chain) => c.chainId === chainId) as Chain, [chainId, chains])

  return {
    chain,
    chains, 
    provider: chain ? new ethers.JsonRpcProvider(chain.rpcUrl) : undefined,
    getChain: ((network: string) => chains?.find((c: Chain) => c.network === network))
  }
}

export function useContractInfo() {
  const { provider, chain } = useChainInfo()
  const { walletProvider } = useWeb3ModalProvider();
  
  const { data: contractInfo, mutate } = useSWR(
    ['/info/contract', chain, provider, walletProvider],
    async () => {
      if (!provider || !chain)
        return undefined
      let providerOrSigner: any = provider
      if (walletProvider) {
          const ethersProvider = new BrowserProvider(walletProvider as Eip1193Provider)
          providerOrSigner = await ethersProvider.getSigner()
      }
      const ethismContract = new ethers.Contract(chain.contractAddress, chain.abi, providerOrSigner);
      return {
        ethismContract,
        platformFee: await ethismContract.CREATE_TOKEN_FEE_AMOUNT(),
        maxLiquidity: ethers.formatEther(await ethismContract.initialTokenLPAmount()),
        maxBuyPercent: await ethismContract.MAX_BUY_PERCENT(),
        initialEthAmount: await ethismContract.initialEthLPAmount(),
        initialTokenAmount: await ethismContract.initialTokenLPAmount(),
      }
    }, {
      refreshInterval: 60000,
    }
  )
  return {
    ...contractInfo, reload: mutate
  }
}

export function useSwitchChain() {
  const [isSwitching, setSwitching] = useState(false)
  const { chain, chains } = useChainInfo()
  const { switchNetwork } = useSwitchNetwork()

  return {
    isWrongChain: !chain,
    isSwitching,
    switchChain: async (chainId?: number) => {
      if (!chains)
        return
      const newChainId = chainId ?? chains[0].chainId
      setSwitching(true)
      await switchNetwork(newChainId).finally(() => setSwitching(false))
    }
  }
}